<template>
  <div class="filter-selector-container" ref="selectorContainer">
    <button class="filter-selector-button" @click.prevent="toggleMenu" ref="selectorButton" :class="{ 'has-filters': hasActiveFilters }">
      <FilterIcon :size="18" />
      <span class="filter-label">Filters</span>
      <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
      <ChevronDownIcon :size="16" class="chevron-icon" :class="{ 'rotated': isMenuOpen }" />
    </button>
    <Teleport to="body">
      <Transition name="menu-slide" mode="out-in">
        <div v-if="isMenuOpen" class="menu-wrapper" :style="menuStyle">
          <FilterSelectorMenu
            ref="filterMenu"
            :selected-filters="modelValue"
            :field-options="fieldOptions"
            @update:filters="handleFiltersUpdate"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import FilterIcon from 'vue-material-design-icons/Filter.vue';
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue';
import FilterSelectorMenu from '../menus/FilterSelectorMenu.vue';

export default {
  name: 'FilterSelector',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    fieldOptions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isMenuOpen: false,
      menuPosition: { top: 0, left: 0 }
    };
  },
  computed: {
    hasActiveFilters() {
      return this.modelValue.length > 0;
    },
    activeFilterCount() {
      return this.modelValue.length;
    },
    menuStyle() {
      return {
        top: `${this.menuPosition.top}px`,
        left: `${this.menuPosition.left}px`
      };
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
  methods: {
    toggleMenu() {
      if (!this.isMenuOpen) {
        const button = this.$refs.selectorButton;
        if (button) {
          const rect = button.getBoundingClientRect();
          this.menuPosition = {
            top: rect.bottom + 8,
            left: rect.left
          };
        }
      }
      this.isMenuOpen = !this.isMenuOpen;
    },
    handleClickOutside(event) {
      const selectorContainer = this.$refs.selectorContainer;
      const clickedSelector = selectorContainer && selectorContainer.contains(event.target);
      const clickedMenu = event.target.closest('.filter-selector-menu');
      const clickedDropdown = event.target.closest('.dropdown-menu');
      const clickedSelectInput = event.target.closest('.select-input');
      const clickedDateInput = event.target.closest('.date-input');
      const clickedMonetaryInput = event.target.closest('.monetary-input');

      if (!clickedSelector && !clickedMenu && !clickedDropdown && !clickedSelectInput && !clickedDateInput && !clickedMonetaryInput) {
        // Emit one final update before closing
        this.$refs.filterMenu?.emitFilters();
        this.isMenuOpen = false;
      }
    },
    handleFiltersUpdate(filters) {
      this.$emit('update:modelValue', filters);
    }
  },
  emits: ['update:modelValue'],
  components: { FilterIcon, ChevronDownIcon, FilterSelectorMenu }
}
</script>

<style scoped>
.filter-selector-container {
  position: relative;
}

.filter-selector-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 14px;
  color: #333;
  height: 32px;
  box-sizing: border-box;
  white-space: nowrap;
}

.filter-selector-button:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.filter-selector-button:active {
  background-color: #ebebeb;
}

.filter-selector-button.has-filters {
  background-color: rgba(67, 56, 202, 0.08);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.filter-selector-button.has-filters:hover {
  background-color: rgba(67, 56, 202, 0.12);
  border-color: var(--primary-hover);
}

.filter-label {
  font-weight: 500;
  user-select: none;
}

.filter-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--primary-color);
  color: white;
  border-radius: var(--border-radius);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.chevron-icon {
  transition: transform 0.2s ease;
  color: #666;
}

.filter-selector-button.has-filters .chevron-icon {
  color: var(--primary-color);
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.menu-wrapper {
  position: fixed;
  z-index: 1000;
}

.menu-slide-enter-active {
  transition: all 0.2s ease-out;
}

.menu-slide-leave-active {
  transition: all 0.15s ease-in;
}

.menu-slide-enter-from {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top left;
}

.menu-slide-leave-to {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top left;
}
</style>
